import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import { formatCardNumber, stripCardNumber } from '../utils/cardNumber';
import { formatExpiryDate, normalizeExpiryDate } from '../utils/expiryDate';

const emptyForm = {
  title: '',
  cardholder_name: '',
  card_number: '',
  expiry_date: '',
  cvv: '',
  note: '',
};

const CardFormModal = ({
  isOpen,
  mode,
  initialValues,
  onClose,
  onSave,
  saving,
  error,
}) => {
  const [form, setForm] = useState(emptyForm);
  const noteRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setForm({
        title: initialValues?.title || '',
        cardholder_name: initialValues?.cardholder_name || '',
        card_number: formatCardNumber(initialValues?.card_number || ''),
        expiry_date: initialValues?.expiry_date || '',
        cvv: initialValues?.cvv || '',
        note: initialValues?.note || '',
      });
    }
  }, [isOpen, initialValues]);

  useEffect(() => {
    const textarea = noteRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [form.note, isOpen]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCardNumberChange = (event) => {
    const digits = stripCardNumber(event.target.value).slice(0, 19);
    setForm((prev) => ({ ...prev, card_number: formatCardNumber(digits) }));
  };

  const handleExpiryChange = (event) => {
    setForm((prev) => ({
      ...prev,
      expiry_date: formatExpiryDate(event.target.value),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave({
      ...form,
      card_number: stripCardNumber(form.card_number),
      expiry_date: normalizeExpiryDate(form.expiry_date),
    });
  };

  const title = mode === 'edit' ? 'Edit card' : 'Add card';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="border border-gray-300 bg-white rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="card-form"
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 py-2 text-sm disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >
      <form id="card-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="card-title" className="block text-sm text-gray-700 mb-1">
            Card name
          </label>
          <input
            id="card-title"
            type="text"
            value={form.title}
            onChange={handleChange('title')}
            required
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-400"
          />
        </div>

        <div>
          <label htmlFor="cardholder-name" className="block text-sm text-gray-700 mb-1">
            Cardholder name
          </label>
          <input
            id="cardholder-name"
            type="text"
            value={form.cardholder_name}
            onChange={handleChange('cardholder_name')}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-400"
          />
        </div>

        <div>
          <label htmlFor="card-number" className="block text-sm text-gray-700 mb-1">
            Card number
          </label>
          <input
            id="card-number"
            type="text"
            inputMode="numeric"
            value={form.card_number}
            onChange={handleCardNumberChange}
            placeholder="9434 5434 5443 8435"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-400"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="expiry-date" className="block text-sm text-gray-700 mb-1">
              Expiry
            </label>
            <input
              id="expiry-date"
              type="text"
              value={form.expiry_date}
              onChange={handleExpiryChange}
              placeholder="MM/YY"
              maxLength={5}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-400"
            />
          </div>
          <div>
            <label htmlFor="cvv" className="block text-sm text-gray-700 mb-1">
              CVV
            </label>
            <input
              id="cvv"
              type="text"
              inputMode="numeric"
              value={form.cvv}
              onChange={handleChange('cvv')}
              maxLength={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-400"
            />
          </div>
        </div>

        <div>
          <label htmlFor="card-note" className="block text-sm text-gray-700 mb-1">
            Note
          </label>
          <textarea
            id="card-note"
            ref={noteRef}
            value={form.note}
            onChange={handleChange('note')}
            rows={1}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none overflow-hidden outline-none focus:border-gray-400"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
      </form>
    </Modal>
  );
};

export default CardFormModal;
